import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Basis, StatusPill, fmt, usd } from "./bits";

export const DailyTable = ({ data, usage, unit }) => {
  const [open, setOpen] = useState({});
  if (!data) return null;
  const budget = data.daily_budget_credits;
  return (
    <div className="rounded-xl border bg-card" data-testid="finance-daily-table">
      <div className="p-4 border-b flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="font-semibold">Daily Consumption</div>
          <div className="text-xs text-muted-foreground">Credits the app needs just to operate — every AI call, grouped by day and feature. Click a day to see individual calls.</div>
        </div>
        <div className="text-xs text-muted-foreground">{budget ? `Daily budget ${fmt(budget)} ${unit}` : "No daily budget set — add one in Settings to get 🟢🟡🔴 status"}</div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-muted-foreground border-b">
            <tr>
              <th className="p-3 text-left">Date</th>
              <th className="p-3 text-left">Feature / Service</th>
              <th className="p-3 text-right">Calls</th>
              <th className="p-3 text-right">Tokens in / out</th>
              <th className="p-3 text-right">Credits used</th>
              <th className="p-3 text-right">Est. cost</th>
              <th className="p-3 text-right">% of day</th>
              <th className="p-3 text-right">% of budget</th>
              <th className="p-3 text-left">Basis</th>
              <th className="p-3 text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {data.days.length === 0 && <tr><td colSpan={10} className="p-6 text-center text-muted-foreground" data-testid="finance-daily-empty">No AI usage in this range.</td></tr>}
            {data.days.map((d) => {
              const rows = data.rows.filter((r) => r.date === d.date);
              const calls = (usage || []).filter((u) => u.date === d.date);
              return [
                <tr key={d.date} className="border-b bg-secondary/40 font-medium cursor-pointer" data-testid={`fin-day-${d.date}`} onClick={() => setOpen((p) => ({ ...p, [d.date]: !p[d.date] }))}>
                  <td className="p-3 flex items-center gap-1">{open[d.date] ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}{d.date}</td>
                  <td className="p-3">Day total</td>
                  <td className="p-3 text-right">{d.calls}{d.failed ? <span className="text-rose-600"> ({d.failed} failed)</span> : ""}</td>
                  <td className="p-3 text-right tabular-nums">{fmt(d.input_tokens, 0)} / {fmt(d.output_tokens, 0)}</td>
                  <td className="p-3 text-right tabular-nums" data-testid={`fin-day-credits-${d.date}`}>{fmt(d.est_credits)}</td>
                  <td className="p-3 text-right tabular-nums">{usd(d.est_cost_usd)}</td>
                  <td className="p-3 text-right">100%</td>
                  <td className="p-3 text-right">{d.pct_of_budget != null ? `${d.pct_of_budget}%` : "—"}</td>
                  <td className="p-3"><Basis basis={d.tokens_basis} /></td>
                  <td className="p-3"><StatusPill status={d.status} /></td>
                </tr>,
                ...rows.map((r) => (
                  <tr key={d.date + r.feature} className="border-b last:border-0 text-muted-foreground" data-testid={`fin-row-${d.date}-${r.feature}`}>
                    <td className="p-3 pl-8" />
                    <td className="p-3 text-foreground">{r.feature}<div className="text-[11px] opacity-70">{r.models.join(", ")}</div></td>
                    <td className="p-3 text-right">{r.calls}</td>
                    <td className="p-3 text-right tabular-nums">{fmt(r.input_tokens, 0)} / {fmt(r.output_tokens, 0)}</td>
                    <td className="p-3 text-right tabular-nums text-foreground">{fmt(r.est_credits)}</td>
                    <td className="p-3 text-right tabular-nums">{usd(r.est_cost_usd)}</td>
                    <td className="p-3 text-right">{r.pct_of_day}%</td>
                    <td className="p-3 text-right">{r.pct_of_budget != null ? `${r.pct_of_budget}%` : "—"}</td>
                    <td className="p-3"><Basis basis={r.tokens_basis} /></td>
                    <td className="p-3 text-xs truncate max-w-[220px]" title={r.sources.join(" | ")}>{r.sources[0]}</td>
                  </tr>
                )),
                open[d.date] && (
                  <tr key={d.date + "-calls"} className="border-b bg-background/40">
                    <td colSpan={10} className="p-3 pl-8">
                      <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Individual calls ({calls.length})</div>
                      <div className="max-h-72 overflow-auto text-xs space-y-0.5" data-testid={`fin-calls-${d.date}`}>
                        {calls.map((c) => (
                          <div key={c.id} className="grid grid-cols-[80px_1fr_130px_90px_70px] gap-2 items-center py-0.5 border-b border-border/40 last:border-0">
                            <span className="text-muted-foreground">{c.ts.slice(11, 19)} UTC</span>
                            <span className="truncate" title={c.task}>{c.feature} — {c.task}{c.ok === false && <span className="text-rose-600"> · failed</span>}</span>
                            <span className="tabular-nums text-muted-foreground">{fmt(c.input_tokens, 0)} / {fmt(c.output_tokens, 0)} tok</span>
                            <span className="tabular-nums text-right">{fmt(c.est_credits)} {unit}</span>
                            <Basis basis={c.actual ? "actual" : "estimated"} />
                          </div>
                        ))}
                        {calls.length === 0 && <div className="text-muted-foreground">Call list limited to the most recent 300 rows in range.</div>}
                      </div>
                    </td>
                  </tr>
                ),
              ];
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
